import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChartBarIcon, ChevronUpIcon, ArchiveBoxIcon, PlusIcon } from '@heroicons/react/24/outline';
import type { SidebarGroup } from '@/features/groupRail/GroupRailListItem';
import type { Poll } from '@flock-types/polls.types';
import { usePolls } from '@/hooks/usePolls';
import { useGroupLeadership } from '@/hooks/useGroupLeadership';
import PollCard from './PollCard';
import PollCreateModal from './PollCreateModal';

interface Props {
  group: SidebarGroup;
}

/** Poll list for a group: active, closed (toggle), archived (leader toggle). */
export default function PollListPane({ group }: Props) {
  const navigate = useNavigate();
  const { polls, loading, error, refetch, createPoll } = usePolls(group.id, group.orgId);
  const isLeader = useGroupLeadership(group.id);
  const [showClosed, setShowClosed] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState(false);

  const { activePolls, closedPolls, archivedPolls } = useMemo(() => {
    const active: Poll[] = [];
    const closed: Poll[] = [];
    const archived: Poll[] = [];
    for (const poll of polls) {
      if (poll.status === 'archived') archived.push(poll);
      else if (poll.status === 'closed') closed.push(poll);
      else active.push(poll);
    }
    return { activePolls: active, closedPolls: closed, archivedPolls: archived };
  }, [polls]);

  const openPoll = (poll: Poll) => navigate(`/polls/${group.id}/p/${poll.id}`);
  const isEmpty = polls.length === 0;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-flock-gray-200 bg-white px-6 py-4">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-semibold text-flock-navy-900">Polls</h1>
          <p className="truncate text-sm text-flock-navy-500">
            {group.name} · {group.orgName}
          </p>
        </div>
        {isLeader && (
          <button
            onClick={() => setCreating(true)}
            className="min-h-touch flex shrink-0 items-center gap-1.5 rounded-lg bg-flock-navy-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-flock-navy-700"
          >
            <PlusIcon className="h-4 w-4" />
            New Poll
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex h-full items-center justify-center text-sm text-flock-navy-400">
            Loading polls…
          </div>
        ) : error ? (
          <div className="flex h-full flex-col items-center justify-center gap-3">
            <p className="text-sm text-flock-navy-500">{error}</p>
            <button
              onClick={() => refetch()}
              className="rounded-lg bg-flock-navy-600 px-4 py-2 text-sm font-medium text-white hover:bg-flock-navy-700"
            >
              Try Again
            </button>
          </div>
        ) : isEmpty ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
            <ChartBarIcon className="h-12 w-12 text-flock-navy-300" />
            <h2 className="text-lg font-semibold text-flock-navy-700">No Polls Yet</h2>
            <p className="max-w-sm text-sm text-flock-navy-500">
              {isLeader
                ? "Create a poll to get your group's opinion on anything."
                : "Your group leader hasn't created any polls yet."}
            </p>
            {isLeader && (
              <button
                onClick={() => setCreating(true)}
                className="mt-4 rounded-lg bg-flock-navy-600 px-6 py-3 text-sm font-semibold text-white hover:bg-flock-navy-700"
              >
                Create Poll
              </button>
            )}
          </div>
        ) : (
          <div className="mx-auto max-w-3xl space-y-3">
            {activePolls.map((poll) => (
              <PollCard key={poll.id} poll={poll} onOpen={openPoll} />
            ))}

            {closedPolls.length > 0 && (
              <button
                onClick={() => setShowClosed((v) => !v)}
                className="mx-auto flex items-center gap-1.5 py-2 text-sm font-medium text-flock-navy-500 hover:text-flock-navy-700"
              >
                {showClosed ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ArchiveBoxIcon className="h-4 w-4" />
                )}
                {showClosed ? 'Hide Closed' : `View Closed (${closedPolls.length})`}
              </button>
            )}
            {showClosed && (
              <>
                <div className="px-1 text-xs font-semibold uppercase tracking-wide text-flock-navy-400">
                  Closed
                </div>
                {closedPolls.map((poll) => (
                  <div key={poll.id} className="opacity-60">
                    <PollCard poll={poll} onOpen={openPoll} />
                  </div>
                ))}
              </>
            )}

            {isLeader && archivedPolls.length > 0 && (
              <button
                onClick={() => setShowArchived((v) => !v)}
                className="mx-auto flex items-center gap-1.5 py-2 text-sm font-medium text-flock-navy-500 hover:text-flock-navy-700"
              >
                {showArchived ? (
                  <ChevronUpIcon className="h-4 w-4" />
                ) : (
                  <ArchiveBoxIcon className="h-4 w-4" />
                )}
                {showArchived ? 'Hide Archived' : `View Archived (${archivedPolls.length})`}
              </button>
            )}
            {isLeader && showArchived && archivedPolls.length > 0 && (
              <>
                <div className="px-1 text-xs font-semibold uppercase tracking-wide text-flock-navy-400">
                  Archived
                </div>
                {archivedPolls.map((poll) => (
                  <div key={poll.id} className="opacity-40">
                    <PollCard poll={poll} onOpen={openPoll} />
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {creating && (
        <PollCreateModal
          createPoll={createPoll}
          onCreated={(pollId) => {
            setCreating(false);
            navigate(`/polls/${group.id}/p/${pollId}`);
          }}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  );
}
